/**
 * Pareceres, alcadas e bloqueios de emissao (C-ALC).
 *
 * Cliente das rotas da secao 7.52 do docs/API.md. Fica dentro da funcionalidade, e
 * nao em shared/api/resources, para nao mexer no arquivo central antes da
 * integracao; o orquestrador pode reexportar daqui.
 */

import { api } from '@/shared/api/client';
import type { Money } from '@/shared/api/types';

export type PolicyKind = 'pareceres' | 'alcadas' | 'bloqueios' | 'fluxo';
export type DecisionType = 'limite_credito' | 'subscricao_is' | 'liberacao_bloqueio';
export type PolicySituation = 'rascunho' | 'agendada' | 'vigente' | 'substituida';

export interface PolicyVersion {
  id: string;
  kind: PolicyKind;
  version: number;
  status: 'rascunho' | 'ativa';
  situation: PolicySituation;
  values: unknown;
  values_hash: string;
  based_on_version: number | null;
  notes: string;
  created_label: string;
  updated_label: string;
  activated_label: string;
  activated_at: string | null;
  effective_from: string | null;
  justification: string;
  created_at: string;
  updated_at: string;
}

export interface PolicyCurrent {
  kind: PolicyKind;
  values: unknown;
  version: number;
  policy_id: string | null;
  is_default: boolean;
  scheduled: PolicyVersion | null;
}

// --- Valores de cada assunto (nomes como o servidor grava) ----------------------

export type OpinionRule = 'facultativo' | 'obrigatorio' | 'nas_situacoes';
export type ReviewSituation =
  | 'motor_analise_manual'
  | 'motor_recusa'
  | 'motor_pendente'
  | 'motor_desvio_para_esteira'
  | 'motor_nao_executado';

export interface ReviewRequirement {
  parecer_escrito: OpinionRule;
  situacoes: ReviewSituation[];
  justificativa_contra_motor: boolean;
}

export interface ReviewPolicyValues {
  pareceres: Record<'credito' | 'subscricao' | 'juridico', ReviewRequirement>;
}

export interface AuthorityLevelValue {
  nivel: number;
  nome: string;
  tipo_titular: 'perfil' | 'permissao';
  titular: string;
  teto: Money | null;
}

export interface AuthorityMatrixValues {
  decisoes: Partial<Record<DecisionType, AuthorityLevelValue[]>>;
  pep_nivel_minimo: Partial<Record<DecisionType, number>>;
}

export type ImpedimentCode =
  | 'limite_insuficiente'
  | 'limite_nao_verificavel'
  | 'contragarantia_pendente'
  | 'cartao_credito_ou_pep_aberto'
  | 'cadastro_vencido'
  | 'parecer_exigido_pendente'
  | 'is_acima_da_maxima'
  | 'modalidade_bloqueada_cnae'
  | 'grupo_sem_limite_automatico'
  | 'avaliacao_manual_obrigatoria'
  | 'motor_recusa_ou_pendente'
  | 'alcada_pendente';

export interface ImpedimentPolicyValues {
  impedimentos: Record<ImpedimentCode, { ligado: boolean; liberavel: boolean }>;
  opcoes: {
    renovacao_confere_limite: 'todas' | 'somente_judicial';
    endosso_dispensa_cadastro_vencido: boolean;
  };
}

export interface EngineFlowValues {
  motor_no_envio_para_analise: boolean;
}

// --- Quadro da operacao ---------------------------------------------------------

export interface AuthorityLevel {
  level: number;
  label: string;
  holder_kind: 'perfil' | 'permissao';
  holder: string;
  ceiling: Money | null;
}

export interface AuthorityStanding {
  decision_type: DecisionType;
  applied: boolean;
  matrix_version: number;
  level: AuthorityLevel | null;
  value?: Money;
  allowed?: boolean;
  required_level?: AuthorityLevel;
  pep_minimum_level?: number;
}

export interface AuthorityRecord {
  applied: boolean;
  decision_type?: string;
  matrix_version?: number;
  level?: number;
  level_label?: string;
  ceiling?: Money;
  note?: string;
  pep_minimum_level?: number;
}

export interface ImpedimentRelease {
  id: string;
  code: string;
  reference: string;
  justification: string;
  value: Money;
  policy_version: number;
  authority: AuthorityRecord;
  released_label: string;
  created_at: string;
}

export interface Impediment {
  code: string;
  message: string;
  reference: string;
  releasable: boolean;
  informational: boolean;
  blocks: boolean;
  source: 'politica_da_empresa' | 'configuracao_da_plataforma';
  details: Record<string, unknown>;
  release: ImpedimentRelease | null;
}

export interface Referral {
  id: string;
  decision_type: DecisionType;
  subject: string;
  requested_status: string;
  value: Money;
  matrix_version: number;
  required_level: number | null;
  required_label: string;
  requester_level: number | null;
  requester_label: string;
  justification: string;
  status: 'pendente' | 'resolvido';
  requested_label: string;
  created_at: string;
  resolved_label: string;
  resolved_at: string | null;
  resolution: string;
}

export interface ReviewHistoryEntry {
  id: string;
  area: 'credito' | 'subscricao' | 'juridico' | 'liberacao_bloqueio';
  event:
    | 'aprovacao'
    | 'reprovacao'
    | 'pendencia'
    | 'em_analise'
    | 'revisao'
    | 'encaminhamento'
    | 'liberacao';
  status: string;
  stage: string;
  reason: string;
  justification: string;
  opinion: string;
  engine_run_id: string | null;
  engine_final_result: string;
  against_engine: boolean;
  value: Money | null;
  authority: AuthorityRecord;
  policy_versions: Record<string, number>;
  actor_label: string;
  created_at: string;
}

export interface EngineSummary {
  run_id: string;
  mode: string;
  final_result: string;
  pendencies: string[];
  simulated: boolean;
  created_at: string;
}

export interface OperationAuthority {
  operation_id: string;
  exposure: Money;
  gate_applies: boolean;
  impediments: Impediment[];
  referrals: Referral[];
  history: ReviewHistoryEntry[];
  my_authority: AuthorityStanding[];
  engine: EngineSummary | null;
  policies: { kind: PolicyKind; version: number }[];
}

const base = '/api/v1/credit-authority/policies';

export const creditAuthority = {
  me: (signal?: AbortSignal) =>
    api.get<AuthorityStanding[]>('/api/v1/credit-authority/me', signal),
  current: (kind: PolicyKind, signal?: AbortSignal) =>
    api.get<PolicyCurrent>(`${base}/${kind}`, signal),
  versions: (kind: PolicyKind, signal?: AbortSignal) =>
    api.get<PolicyVersion[]>(`${base}/${kind}/versions`, signal),
  createDraft: (kind: PolicyKind, notes = '') =>
    api.post<PolicyVersion>(`${base}/${kind}/versions`, { notes }),
  updateDraft: (kind: PolicyKind, versionId: string, values: unknown, notes?: string) =>
    api.patch<PolicyVersion>(`${base}/${kind}/versions/${versionId}`, {
      values,
      ...(notes !== undefined ? { notes } : {}),
    }),
  activate: (
    kind: PolicyKind,
    versionId: string,
    justification: string,
    effectiveFrom?: string,
  ) =>
    api.post<PolicyVersion>(`${base}/${kind}/versions/${versionId}/activation`, {
      justification,
      ...(effectiveFrom ? { effective_from: effectiveFrom } : {}),
    }),
  operation: (operationId: string, signal?: AbortSignal) =>
    api.get<OperationAuthority>(
      `/api/v1/operations/${operationId}/credit-authority`,
      signal,
    ),
  release: (operationId: string, code: string, justification: string) =>
    api.post<ImpedimentRelease>(
      `/api/v1/operations/${operationId}/issuance-impediments/${code}/release`,
      { justification },
    ),
};

// --- Rotulos da tela -------------------------------------------------------------

export const decisionLabels: Record<DecisionType, string> = {
  limite_credito: 'Limite de credito concedido',
  subscricao_is: 'Subscricao pela importancia segurada',
  liberacao_bloqueio: 'Liberacao de impedimento de emissao',
};

export const impedimentLabels: Record<ImpedimentCode, string> = {
  limite_insuficiente: 'Limite disponivel insuficiente',
  limite_nao_verificavel: 'Limite nao verificavel',
  contragarantia_pendente: 'Contragarantia (CCG) pendente',
  cartao_credito_ou_pep_aberto: 'Cartao aberto na esteira de credito ou PEP',
  cadastro_vencido: 'Cadastro do tomador vencido',
  parecer_exigido_pendente: 'Parecer exigido pendente',
  is_acima_da_maxima: 'Importancia segurada acima da maxima',
  modalidade_bloqueada_cnae: 'Modalidade bloqueada para o CNAE',
  grupo_sem_limite_automatico: 'Grupo sem limite automatico',
  avaliacao_manual_obrigatoria: 'Avaliacao manual obrigatoria',
  motor_recusa_ou_pendente: 'Recusa ou pendencia homologada do motor',
  alcada_pendente: 'Decisao pendente no nivel acima de alcada',
};

/** Fonte documental de cada impedimento, a mesma do servidor. */
export const impedimentSources: Record<ImpedimentCode, string> = {
  limite_insuficiente: 'A05 Credito!D151 e D155 (LIM-008, CRD-014)',
  limite_nao_verificavel: 'Contrato do limite por dimensao (C-LIM)',
  contragarantia_pendente: 'A05 Politica de Subscricao!D74 (LIM-011)',
  cartao_credito_ou_pep_aberto: 'MRC-BLQ-05; 41, PM-09',
  cadastro_vencido: 'A05 Cadastro Tomador!D23 (CAD-014); endosso: CAD-021',
  parecer_exigido_pendente: 'MRC-BLQ-05; ADR 0007',
  is_acima_da_maxima: 'A05 Cadastro Modalidades!O325 e K325 (LIM-007, LIM-006)',
  modalidade_bloqueada_cnae: 'A06 Modalidade Restrita!A3 (CAD-005)',
  grupo_sem_limite_automatico: 'A05 Credito!G201:K208 (CRD-008)',
  avaliacao_manual_obrigatoria: 'A05 Credito!D166 (CRD-015)',
  motor_recusa_ou_pendente: '41, secao 1.2',
  alcada_pendente: '41, secao 6',
};

/** Impedimentos que admitem liberacao (domain.ImpedimentReleasable). */
export const releasableImpediments: ImpedimentCode[] = [
  'limite_insuficiente',
  'limite_nao_verificavel',
  'contragarantia_pendente',
  'is_acima_da_maxima',
  'modalidade_bloqueada_cnae',
  'motor_recusa_ou_pendente',
];

export const impedimentOrder: ImpedimentCode[] = [
  'limite_insuficiente',
  'limite_nao_verificavel',
  'contragarantia_pendente',
  'cartao_credito_ou_pep_aberto',
  'cadastro_vencido',
  'parecer_exigido_pendente',
  'is_acima_da_maxima',
  'modalidade_bloqueada_cnae',
  'grupo_sem_limite_automatico',
  'avaliacao_manual_obrigatoria',
  'motor_recusa_ou_pendente',
  'alcada_pendente',
];

export const eventLabels: Record<ReviewHistoryEntry['event'], string> = {
  aprovacao: 'Aprovacao',
  reprovacao: 'Reprovacao',
  pendencia: 'Pendencia',
  em_analise: 'Em analise',
  revisao: 'Revisao',
  encaminhamento: 'Encaminhamento ao nivel acima',
  liberacao: 'Liberacao de impedimento',
};

export const areaLabels: Record<ReviewHistoryEntry['area'], string> = {
  credito: 'Credito',
  subscricao: 'Subscricao',
  juridico: 'Juridico',
  liberacao_bloqueio: 'Impedimento de emissao',
};

export const situationLabels: Record<ReviewSituation, string> = {
  motor_analise_manual: 'Motor em analise manual',
  motor_recusa: 'Motor recusou ou recomendou recusa',
  motor_pendente: 'Motor com pendencia',
  motor_desvio_para_esteira: 'Motor mandou para a esteira da area',
  motor_nao_executado: 'Motor nao executado',
};

export const policySituationLabels: Record<PolicySituation, string> = {
  rascunho: 'Rascunho',
  agendada: 'Agendada',
  vigente: 'Vigente',
  substituida: 'Substituida',
};
